import removeMd from 'remove-markdown'
import useMedia from 'use-media'

import {
  Box,
  Columns,
  Col,
  Icon,
  Heading,
  Label,
  Link,
  Text,
  mediaQueries,
} from '@redriverpress/system'

import getInitialState from 'Common/utils/getInitialState'

import HitWrapper from './HitWrapper'
import HitImage from './HitImage'
import LevelsAndAudiences from './LevelsAndAudiences'
import labels from '../../constants/labels'
import {
  descriptionCharLimitDefault,
  descriptionCharLimitMd,
} from '../../constants/ui'
import { getLessonLink } from '../../utils/hitLinks'
import { lessonPropShape } from '../../constants/propShapes'
import getHitLevelsByType from '../../utils/getHitLevelsByType'
import getPreferredLevelType from '../../utils/getPreferredLevelType'

export default function LessonHit({ hit }) {
  const {
    audiences,
    lesson_section_slug: lessonSectionSlug,
    lesson_section_title: lessonSectionTitle,
    description,
    has_audio: hasAudio,
    has_video: hasVideo,
    image_url: imageUrl,
    labels: labelsList,
    levels,
    slug,
    is_resource: isResource,
    teacher_discretion: teacherDiscretion,
    title,
  } = hit

  const isMd = useMedia(mediaQueries.md)

  const cleanDescription = removeMd(description)

  const descriptionCharLimit = isMd
    ? descriptionCharLimitMd
    : descriptionCharLimitDefault

  const { levelPreference, isUserLoggedIn, isUserFree } = getInitialState()

  const hitLevels = getHitLevelsByType(levelPreference, hit)

  // If the user's level preference isn't Ellii's,
  // but there are no levels on this hit for their level type,
  // use Ellii's levels.
  // Otherwise, use their level preference.
  const preferredLevelType = getPreferredLevelType(levelPreference, hitLevels)

  const lessonLabels = isUserFree
    ? labelsList
    : labelsList.filter(l => l !== 'free' && l !== 'sample_label')

  return (
    <Link
      reset
      href={getLessonLink({ lessonSectionSlug, slug, isUserLoggedIn })}
    >
      <HitWrapper px={3} py={[3, null, 4]} mb={[3, null, 4]}>
        <Columns>
          <Col width={[1, null, 1 / 4]}>
            <HitImage src={imageUrl} />
          </Col>
          <Col width={[1, null, 3 / 4]}>
            <Box mb={3} ml={-1}>
              <Label compact type='materialType' m={1}>
                <Icon icon={isResource ? 'wrench' : 'file-alt'} pr={2} />
                {isResource ? 'Resource' : 'Lesson'}
              </Label>
              {lessonLabels.map(label => (
                <Label compact type={label} m={1}>
                  <Icon icon={labels[label].icon} pr={2} />
                  {labels[label].text}
                </Label>
              ))}
              {hasAudio && (
                <Label compact m={1}>
                  <Icon icon='volume-up' pr={2} />
                  Has Audio
                </Label>
              )}
              {hasVideo && (
                <Label compact m={1}>
                  <Icon icon='video' pr={2} />
                  Has Video
                </Label>
              )}
              {teacherDiscretion && (
                <Label compact type='warning'>
                  <Icon icon='exclamation-triangle' pr={2} />
                  Teacher Discretion
                </Label>
              )}
            </Box>
            <Heading as='h4' mb={1}>
              {title}
            </Heading>
            <Text fontWeight={600}>{lessonSectionTitle}</Text>
            <Box my={3}>
              <LevelsAndAudiences
                audiences={audiences}
                eslLevels={levels}
                preferredLevels={hitLevels}
                preferredLevelType={preferredLevelType}
              />
            </Box>
            {description && (
              <Text fontSize={0} measure>
                {cleanDescription.length > descriptionCharLimit
                  ? `${cleanDescription.substring(0, descriptionCharLimit)}…`
                  : cleanDescription}
              </Text>
            )}
          </Col>
        </Columns>
      </HitWrapper>
    </Link>
  )
}

LessonHit.propTypes = {
  hit: lessonPropShape.isRequired,
}
